// @ts-nocheck
/**
 * View Blacklist / DND — dual tab (blacklist/dnd) with independent loaders,
 * delete via deleteApiCallWithHeader for blacklist, POST DND_ADD with
 * operation:'remove' for DND.
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Loader2, Trash2, Plus, Search as SearchIcon, Ban, PhoneOff, RefreshCw } from 'lucide-react';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { deleteApiCallWithHeader } from '@/service/deleteApiCall';
import {
  API_DND_LIST,
  API_DND_ADD,
  API_BLACKLIST_LIST,
  API_BLACKLIST_DELETE,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { ROUTES } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';

const formatDate = (val: any) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

const ListTable = ({ loading, data, onDelete, tone }: any) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading numbers…
      </div>
    );
  }

  const rowsEmpty = !data || data.length === 0;

  return (
    <div className="overflow-x-auto rounded-lg border border-border/60 bg-card">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-primary/[0.06] text-[10px] uppercase tracking-wide text-muted-foreground">
            <th className="px-3 py-2 text-left font-semibold">ID</th>
            <th className="px-3 py-2 text-left font-semibold">Mobile Number</th>
            <th className="px-3 py-2 text-left font-semibold">Created At</th>
            <th className="px-3 py-2 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rowsEmpty ? (
            <tr>
              <td colSpan={4} className="px-3 py-8 text-center text-sm text-muted-foreground">
                No numbers found.
              </td>
            </tr>
          ) : (
            data.map((item: any, idx: number) => (
              <tr
                key={item.id}
                className={`border-t border-border/40 ${idx % 2 ? 'bg-muted/30' : ''}`}
              >
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                  {item.id}
                </td>
                <td className="px-3 py-2 font-mono text-sm tracking-wider text-foreground">
                  {item.mobile}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {formatDate(item.created_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    title={`Remove from ${tone === 'dnd' ? 'DND' : 'Blacklist'}`}
                    onClick={() => onDelete(item)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const ViewBlacklistDnd = () => {
  const [activeTab, setActiveTab] = useState('blacklist');
  const [blacklistData, setBlacklistData] = useState<any[]>([]);
  const [dndData, setDndData] = useState<any[]>([]);
  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [dndLoading, setDndLoading] = useState(false);

  const fetchBlacklist = async () => {
    setBlacklistLoading(true);
    try {
      const user = getUser();
      const data: any = await getApiCall(API_BLACKLIST_LIST(user?.id), {});
      setBlacklistData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (typeof err === 'string' && err.trim())
        toast.error(friendlyApiError(err) || 'Failed to fetch blacklist');
      setBlacklistData([]);
    } finally {
      setBlacklistLoading(false);
    }
  };

  const fetchDnd = async () => {
    setDndLoading(true);
    try {
      const data: any = await getApiCall(API_DND_LIST, {});
      setDndData(Array.isArray(data) ? data : []);
    } catch (err: any) {
      if (typeof err === 'string' && err.trim())
        toast.error(friendlyApiError(err) || 'Failed to fetch DND list');
      setDndData([]);
    } finally {
      setDndLoading(false);
    }
  };

  const handleDeleteBlacklist = async (item: any) => {
    try {
      await deleteApiCallWithHeader(API_BLACKLIST_DELETE(item.id));
      toast.success('Number removed from Blacklist');
      fetchBlacklist();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to delete');
    }
  };

  const handleDeleteDnd = async (item: any) => {
    try {
      await postApiCall(API_DND_ADD, { mobile: item.mobile, operation: 'remove' });
      toast.success('Number removed from DND list');
      fetchDnd();
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Failed to delete');
    }
  };

  useEffect(() => {
    fetchBlacklist();
    fetchDnd();
  }, []);

  const refreshActive = () => {
    if (activeTab === 'blacklist') fetchBlacklist();
    else fetchDnd();
  };

  const isRefreshing = activeTab === 'blacklist' ? blacklistLoading : dndLoading;

  return (
    <FillPage>
      <PageHeader
        title="Blacklist / DND"
        description="Numbers currently on the Blacklist or DND list. Add via Manage."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              disabled={isRefreshing}
              onClick={refreshActive}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={isRefreshing ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.NUMBER_OPERATIONS.SEARCH_DND_BLIST}`}>
                <SearchIcon className="h-4 w-4" />
                Search
              </Link>
            </Button>
            <Button asChild>
              <Link to={`/${ROUTES.NUMBER_OPERATIONS.MANAGE_BLACKLIST_DND}`}>
                <Plus className="h-4 w-4" />
                Add number
              </Link>
            </Button>
          </>
        }
      />

      <div className="mb-3 flex gap-2">
        {[
          { key: 'blacklist', label: 'Blacklist', icon: Ban, count: blacklistData.length, tone: 'rose' },
          { key: 'dnd', label: 'DND List', icon: PhoneOff, count: dndData.length, tone: 'amber' },
        ].map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          const activeCls =
            tab.tone === 'rose'
              ? 'border-rose-500 bg-rose-500 text-white shadow-md ring-2 ring-rose-500/25'
              : 'border-amber-500 bg-amber-500 text-white shadow-md ring-2 ring-amber-500/25';
          const idleCls =
            tab.tone === 'rose'
              ? 'border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-400 hover:bg-rose-100 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300'
              : 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300';
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveTab(tab.key)}
              className={
                'relative flex h-10 items-center gap-2 rounded-lg border-2 px-4 text-sm font-bold transition ' +
                (active ? activeCls : idleCls)
              }
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              <span
                className={
                  'ml-1 inline-flex min-w-[22px] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ' +
                  (active
                    ? 'bg-white/90 text-foreground'
                    : tab.tone === 'rose'
                      ? 'bg-rose-200/70 text-rose-800 dark:bg-rose-900/60 dark:text-rose-200'
                      : 'bg-amber-200/70 text-amber-800 dark:bg-amber-900/60 dark:text-amber-200')
                }
              >
                {tab.count}
              </span>
            </button>
          );
        })}
      </div>

      <div
        className="rounded-2xl border border-border/60 bg-card p-4"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        {activeTab === 'blacklist' && (
          <ListTable
            loading={blacklistLoading}
            data={blacklistData}
            onDelete={handleDeleteBlacklist}
            tone="blacklist"
          />
        )}
        {activeTab === 'dnd' && (
          <ListTable
            loading={dndLoading}
            data={dndData}
            onDelete={handleDeleteDnd}
            tone="dnd"
          />
        )}
      </div>
    </FillPage>
  );
};

export default ViewBlacklistDnd;
